import { formatCurrency, toNumber } from './currency';

// Most a Non-Billable Repair may pay a contractor, per repair. Local to the
// admin payout utils until the backend handoff promotes it to the shared
// package.
export const NON_BILLABLE_PAYOUT_LIMIT = 150.0;

// Exact UX copy — asserted verbatim by the dialog tests.
export const PAYOUT_AMOUNT_GREATER_THAN_ZERO_MESSAGE =
  'Please enter a Payout Amount greater than $0.00.';
export const PAYOUT_AMOUNT_EXCEEDS_PO_TOTAL_MESSAGE =
  'The Payout Amount cannot exceed the PO Total, please adjust before proceeding.';
export const PAYOUT_AMOUNT_AT_LEAST_ZERO_MESSAGE =
  'Please enter a Payout Amount of $0.00 or more.';
export const PAYOUT_AMOUNT_EXCEEDS_NON_BILLABLE_LIMIT_MESSAGE = `Value is higher than the Non-Billable limit of ${formatCurrency(
  NON_BILLABLE_PAYOUT_LIMIT,
)}, please adjust before proceeding.`;

export type ValidatePayoutAmountInput = {
  // The manually entered Payout Amount, as typed (currency input value) or
  // already coerced to a number.
  value: number | string | null | undefined;
  // The repair's PO Total; Postgres numerics arrive as strings.
  poTotal: number | string | null | undefined;
};

// Currency comparison in cents, so 120.00 vs 120 and float noise
// (0.1 + 0.2) never produce a spurious "exceeds PO Total".
const toCents = (value: number): number => Math.round(value * 100);

// A blank entry is Number()-coerced to 0, which non-billable mode would accept
// as a valid $0.00; treat whitespace as the empty field it is.
const toEnteredAmount = (
  value: number | string | null | undefined,
): number | null => toNumber(typeof value === 'string' ? value.trim() : value);

export type Billability = 'billable' | 'nonBillable' | 'unknown';

// Classifies a repair by its PO Total — the single non-billability signal:
// the backend forces a Non-Billable Repair's PO Total to exactly
// $0.00 and rejects billable repairs below $0.01, and one dialog's sheet rows
// don't carry the nonBillable flag. Missing, non-numeric or (server-impossible)
// negative is 'unknown' — data not loaded, which must never grant payout
// editing by accident.
export const billabilityFromPoTotal = (
  poTotal: number | string | null | undefined,
): Billability => {
  const po = toNumber(poTotal);
  if (po === null) return 'unknown';
  const cents = toCents(po);
  if (cents === 0) return 'nonBillable';
  return cents > 0 ? 'billable' : 'unknown';
};

// Returns the message to render as inline error helperText, or null when the
// value is acceptable. The cap is inclusive and there is no minimum floor
// above $0.00 — a value below the calculated amount records a discount. A
// missing/non-numeric PO Total leaves the cap unenforceable, so only the
// greater-than-zero rule applies; the backend's own <= PO Total check is the
// backstop and surfaces through the API-error toast path. usePayoutAmount
// pre-gates editing on a 'billable' classification, so that branch is
// defensive.
export const validatePayoutAmount = ({
  value,
  poTotal,
}: ValidatePayoutAmountInput): string | null => {
  const amount = toEnteredAmount(value);

  if (billabilityFromPoTotal(poTotal) === 'nonBillable') {
    if (amount === null || toCents(amount) < 0) {
      return PAYOUT_AMOUNT_AT_LEAST_ZERO_MESSAGE;
    }
    if (toCents(amount) > toCents(NON_BILLABLE_PAYOUT_LIMIT)) {
      return PAYOUT_AMOUNT_EXCEEDS_NON_BILLABLE_LIMIT_MESSAGE;
    }
    return null;
  }

  if (amount === null || toCents(amount) <= 0) {
    return PAYOUT_AMOUNT_GREATER_THAN_ZERO_MESSAGE;
  }

  const po = toNumber(poTotal);
  if (po === null) return null;

  if (toCents(amount) > toCents(po)) {
    return PAYOUT_AMOUNT_EXCEEDS_PO_TOTAL_MESSAGE;
  }

  return null;
};
